import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../contexts/AuthContext";
import { Radio, Loader2, Save, Plus, Trash2, Pencil, X, Eye, EyeOff, ExternalLink } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5";
const btnCls =
  "inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md font-medium transition-colors disabled:opacity-60";
const EMPTY = { title: "", youtube_url: "", description: "", is_live: false, scheduled_at: "" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const toIso = (local) => (local ? new Date(local).toISOString() : "");

function PageSettings({ onMsg }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/live/settings").then(({ data }) => setS(data)).catch((e) => onMsg(formatApiError(e), true));
  }, [onMsg]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/live/settings", patch);
      setS(data);
      onMsg("Live page settings saved");
    } catch (e) {
      onMsg(formatApiError(e), true);
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-[#FF9933]" />;
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-5" data-testid="live-settings-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Public Page Visibility</h2>
          <p className="text-xs text-gray-500 mt-0.5">When hidden, the "Live" menu link disappears and /live shows an unavailable notice.</p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={() => save({ page_enabled: !s.page_enabled })}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md font-medium border transition-colors ${
            s.page_enabled ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100" : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
          }`}
          data-testid="live-page-visibility-toggle"
        >
          {s.page_enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          {s.page_enabled ? "Visible (click to hide)" : "Hidden (click to show)"}
        </button>
      </div>
      <div className="border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Offline Card: Next Broadcast</h3>
        <p className="text-xs text-gray-500 mb-3">Shown to visitors when nothing is On Air.</p>
        <div className="grid sm:grid-cols-[1fr_220px_auto] gap-3 items-end">
          <div>
            <label className={labelCls}>Title</label>
            <input className={inputCls} value={s.next_broadcast_title || ""} onChange={(e) => setS({ ...s, next_broadcast_title: e.target.value })} placeholder="Shiv Mahapuran Katha" data-testid="next-title-input" />
          </div>
          <div>
            <label className={labelCls}>Scheduled Time</label>
            <input type="datetime-local" className={inputCls} value={toLocalInput(s.next_broadcast_at)} onChange={(e) => setS({ ...s, next_broadcast_at: toIso(e.target.value) })} data-testid="next-time-input" />
          </div>
          <button type="button" className={btnCls} disabled={saving} onClick={() => save({ next_broadcast_title: s.next_broadcast_title || "", next_broadcast_at: s.next_broadcast_at || "" })} data-testid="save-next-broadcast-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </section>
  );
}

function BroadcastForm({ initial, onSubmit, onCancel, busy }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, scheduled_at: f.scheduled_at || "" }); }}
      className="bg-white border border-gray-200 rounded-lg p-6 space-y-4"
      data-testid="broadcast-form"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Title *</label>
          <input className={inputCls} value={f.title} onChange={set("title")} required data-testid="broadcast-title-input" />
        </div>
        <div>
          <label className={labelCls}>YouTube URL *</label>
          <input className={inputCls} value={f.youtube_url} onChange={set("youtube_url")} required placeholder="https://www.youtube.com/watch?v=..." data-testid="broadcast-url-input" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Short Description</label>
        <textarea rows={2} className={`${inputCls} resize-none`} value={f.description} onChange={set("description")} data-testid="broadcast-description-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 items-end">
        <div>
          <label className={labelCls}>Scheduled / Recorded Time (optional)</label>
          <input type="datetime-local" className={inputCls} value={toLocalInput(f.scheduled_at)} onChange={(e) => setF({ ...f, scheduled_at: toIso(e.target.value) })} data-testid="broadcast-scheduled-input" />
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-900 cursor-pointer pb-2">
          <input type="checkbox" checked={f.is_live} onChange={set("is_live")} className="w-4 h-4 accent-red-600" data-testid="broadcast-onair-checkbox" />
          On Air now (replaces any current live stream)
        </label>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50" data-testid="broadcast-cancel-btn">
            Cancel
          </button>
        )}
        <button type="submit" disabled={busy} className={btnCls} data-testid="broadcast-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Broadcast
        </button>
      </div>
    </form>
  );
}

function BroadcastRow({ b, onToggle, onEdit, onDelete }) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-3 p-4" data-testid={`admin-broadcast-row-${b.id}`}>
      <img src={`https://i.ytimg.com/vi/${b.video_id}/default.jpg`} alt="" className="w-24 h-16 object-cover rounded flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{b.title}</p>
        <a href={b.youtube_url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-[#FF9933] inline-flex items-center gap-1 truncate max-w-full">
          {b.youtube_url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
        </a>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onToggle(b)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full font-semibold border ${
            b.is_live ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-600 border-gray-300 hover:border-red-400 hover:text-red-600"
          }`}
          data-testid={`toggle-onair-${b.id}`}
        >
          <Radio className="w-3.5 h-3.5" /> {b.is_live ? "On Air" : "Offline"}
        </button>
        <button onClick={() => onEdit(b)} className="p-2 text-gray-500 hover:text-[#FF9933]" aria-label="Edit" data-testid={`edit-broadcast-${b.id}`}>
          <Pencil className="w-4 h-4" />
        </button>
        <button onClick={() => onDelete(b)} className="p-2 text-gray-500 hover:text-red-600" aria-label="Delete" data-testid={`delete-broadcast-${b.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}

export default function LiveBroadcastManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const notify = useCallback((text, isError = false) => {
    setMsg({ text, isError });
    setTimeout(() => setMsg(null), 3000);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/live/broadcasts");
      setItems(data.items);
    } catch (e) {
      notify(formatApiError(e), true);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const submit = async (form) => {
    setBusy(true);
    try {
      if (editing?.id) await adminApi.patch(`/admin/live/broadcasts/${editing.id}`, form);
      else await adminApi.post("/admin/live/broadcasts", form);
      setEditing(null);
      notify("Broadcast saved");
      load();
    } catch (e) {
      notify(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (b) => {
    try {
      await adminApi.patch(`/admin/live/broadcasts/${b.id}`, { is_live: !b.is_live });
      load();
    } catch (e) {
      notify(formatApiError(e), true);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/live/broadcasts/${b.id}`);
      load();
    } catch (e) {
      notify(formatApiError(e), true);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="live-manager-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>Live Broadcast</h1>
          <p className="text-sm text-gray-500 mt-1">Control the YouTube stream and playlist on the public /live page.</p>
        </div>
        <div className="flex gap-2">
          <a href="/live" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-white" data-testid="view-live-page-link">
            <ExternalLink className="w-4 h-4" /> View Page
          </a>
          {!editing && (
            <button className={btnCls} onClick={() => setEditing(EMPTY)} data-testid="add-broadcast-btn">
              <Plus className="w-4 h-4" /> Add Broadcast
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div className={`px-4 py-2.5 rounded-md text-sm border ${msg.isError ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700"}`} data-testid="live-manager-message">
          {msg.text}
        </div>
      )}

      <PageSettings onMsg={notify} />

      {editing && (
        <div className="relative">
          <button onClick={() => setEditing(null)} className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-700" aria-label="Close form" data-testid="close-broadcast-form">
            <X className="w-4 h-4" />
          </button>
          <BroadcastForm key={editing.id || "new"} initial={{ ...EMPTY, ...editing }} onSubmit={submit} onCancel={() => setEditing(null)} busy={busy} />
        </div>
      )}

      <section className="bg-white border border-gray-200 rounded-lg" data-testid="broadcast-list">
        <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">All Broadcasts ({items.length})</div>
        {loading ? (
          <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#FF9933]" /></div>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center" data-testid="broadcast-list-empty">No broadcasts yet. Click "Add Broadcast" to create one.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((b) => (
              <BroadcastRow key={b.id} b={b} onToggle={toggle} onEdit={setEditing} onDelete={remove} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
